// Login.styles.ts
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

/** ===== Brand / Theme ===== */
const BRAND = "#B79931";
const BRAND_DARK = "#6E5B1F";
const BG_NEUTRAL = "#F6F7F9";

/** ===== Global: reduzir/posicionar reCAPTCHA (v3) ===== */
export const CaptchaStyle = createGlobalStyle`
  .grecaptcha-badge {
    transform: scale(.82);
    transform-origin: 100% 100%;
    right: 10px !important;
    bottom: 10px !important;
    z-index: 2 !important;
  }
  @media (max-width: 480px) {
    .grecaptcha-badge {
      transform: scale(.72);
      right: 6px !important;
      bottom: 6px !important;
    }
  }
`;

/** ===== Layout ===== */
export const Page = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0,1.1fr) minmax(0,1fr);
  gap: clamp(12px, 4vw, 40px);
  background: ${BG_NEUTRAL};
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;
export const LeftPanel = styled.aside<{ bg?: string }>`
  position: relative;
  color: #fff;
  display: grid;
  align-content: center;
  padding: clamp(18px, 5vw, 72px);

  /* 1) overlay direcional + 2) imagem */
  background:
    linear-gradient(
      90deg,
      rgba(15,15,15,.82) 0%,
      rgba(15,15,15,.68) 28%,
      rgba(183,153,49,.36) 70%,
      rgba(183,153,49,.18) 100%
    ),
    ${({ bg }) =>
      bg
        ? `url(${bg})`
        : `linear-gradient(135deg, #B79931 0%, #bdaa57 70%)`};
  background-size: cover;
  background-position: 50% center;
  background-repeat: no-repeat;

  /* 2) vignette suave nas bordas */
  &::after{
    content: "";
    position: absolute; inset: 0;
    top: 0;
    pointer-events: none;
    background:
      radial-gradient(1200px 100% at -10% 50%, rgba(0,0,0,.45) 0%, rgba(0,0,0,0) 48%),
      radial-gradient(1200px 100% at 110% 50%, rgba(0,0,0,.18) 0%, rgba(0,0,0,0) 52%);
  }

  @media (max-width: 980px) {
    min-height: 40vh;
    background-position: center;
  }
  @media (max-width: 480px) {
    min-height: 36vh;
    padding: 20px;
  }
`;

export const LoginForm = styled.form`
  max-height: none !important;
  overflow: visible !important;
  overflow-x: hidden !important;   /* mata a barrinha/setinhas */
  overflow-y: visible !important;
  padding: 0 !important;           /* seu spacing já vem do Card */

  /* esconde scrollbars caso algum filho ainda tente criar */
  -ms-overflow-style: none !important;   /* IE/Edge legados */
  scrollbar-width: none !important;      /* Firefox */
  &::-webkit-scrollbar {
    display: none !important; width: 0 !important; height: 0 !important;
  }
`;

/* limita a largura do conteúdo */
export const LeftInner = styled.div`
  max-width: 680px;
`;

/* 3) cartão translúcido/blur atrás do texto */
export const TextSurface = styled.div`
  display: inline-block;
  padding: clamp(12px, 2.4vw, 22px) clamp(14px, 3vw, 28px);
  border-radius: 16px;
  background: rgba(10, 10, 10, 0.42);
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 10px 28px rgba(0,0,0,.35);
  backdrop-filter: blur(3.5px);
  -webkit-backdrop-filter: blur(3.5px);

  @media (max-width: 980px) { background: rgba(10, 10, 10, 0.46); }
`;

export const BrandRow = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 10px;
  img.logo-notify {
    width: clamp(40px, 6vw, 52px);
    height: clamp(40px, 6vw, 52px);
    object-fit: contain; background:#fff; border-radius: 12px; padding: 6px;
    box-shadow: 0 6px 18px rgba(0,0,0,0.18);
  }
  .brand-name {
    font-weight: 800; font-size: clamp(16px, 2vw, 18px); letter-spacing: .2px;
    color:#fff; text-shadow: 0 1px 2px rgba(0,0,0,.35);
  }
`;

// export const RightIntro = styled.div`
//   width: 100%;
//   max-width: 420px;
//   margin-bottom: 12px;
// `;

// export const RightKicker = styled.div`
//   font-weight: 800;
//   font-size: 12px;
//   letter-spacing: .3px;
//   color: #6b7280;
//   margin-bottom: 4px;
// `;

// export const RightTitle = styled.h3`
//   margin: 0 0 6px;
//   font-size: clamp(18px, 2.6vw, 22px);
//   font-weight: 800;
//   color: #1f2937;
// `;

// export const RightTagline = styled.p`
//   margin: 0;
//   font-size: 13px;
//   line-height: 1.55;
//   color: #4b5563;
// `;

export const Headline = styled.h1`
  color:#fff;
  font-size: clamp(24px, 4.2vw, 40px);
  line-height: 1.15;
  font-weight: 800;
  margin: 6px 0 10px;
  text-shadow: 0 2px 10px rgba(0,0,0,.35);
`;

export const Tagline = styled.p`
  color: rgba(255,255,255,.92);
  font-size: clamp(13px, 1.8vw, 16px);
  line-height: 1.6;
  max-width: 640px;

  @media (max-width: 480px) {
    display: -webkit-box;
    -webkit-line-clamp: 4;   /* até 4 linhas no mobile */
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const RightPanel = styled.main`
  display: grid;
  place-items: center;
  padding: clamp(16px, 5vw, 60px);
  padding-bottom: 80px; /* espaço p/ reCAPTCHA no canto */
`;

export const Card = styled.section`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 16px 38px rgba(16, 24, 40, 0.16);
  padding: 24px;
  overflow-x: hidden !important;
`;


export const FooterLogo = styled.div`
  display: grid; place-items: center; margin-top: 18px;
  img.logo-petropolis { height: 26px; width: auto; opacity: 0.95; }
`;

/** ===== UI base ===== */
export const Title = styled.h2`
  font-size: clamp(18px, 2.4vw, 20px);
  color: #1f2937;
  margin: 8px 0 16px;
  text-align: center;
`;

export const Tabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #f0f3f7;
  border-radius: 12px;
  padding: 4px; gap: 4px; margin-bottom: 14px;
`;

export const TabBtn = styled.button<{ active?: boolean }>`
  appearance: none; border: none; border-radius: 10px; padding: 10px 12px; font-weight: 700; cursor: pointer;
  background: ${({ active }) => (active ? "#fff" : "transparent")};
  color: ${({ active }) => (active ? "#111827" : "#6b7280")};
  box-shadow: ${({ active }) => (active ? "0 2px 10px rgba(0,0,0,0.08)" : "none")};
  transition: background .2s, color .2s, box-shadow .2s;
`;

export const Field = styled.input<{ hasError?: boolean }>`
  width: 100%; padding: 12px; font-size: 16px; border-radius: 10px;
  border: 1.6px solid ${({ hasError }) => (hasError ? "#e53935" : "#cfd8dc")};
  outline: none; transition: border-color .2s, box-shadow .2s; margin-bottom: 12px;
  &:focus { border-color: ${BRAND}; box-shadow: 0 0 0 4px rgba(183,153,49,0.16); }
  &::placeholder { color: #9aa6af; }
`;

export const PasswordFieldWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
  ${Field} { margin-bottom: 0; padding-right: 48px; }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-size: 22px;
  cursor: pointer;
  &:hover { color: ${BRAND_DARK}; }
  &:focus-visible { outline: 2px solid ${BRAND}; outline-offset: -2px; }
`;

export const Hint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 12px;   
  line-height: 1.4;  
`;

export const Row = styled.div`
  display: flex; gap: 10px; min-width: 0; > * { min-width: 0; }
`;


export const Btn = styled.button.attrs<{
  variant?: "primary" | "light";
  type?: "button" | "submit";
}>((props) => ({ type: props.type ?? "button" }))<{
  variant?: "primary" | "light";
}>`
  flex: 1;
  padding: 12px 0;
  font-size: 15px;
  font-weight: 800;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: transform .02s ease, background-color .2s, color .2s;
  ${({ variant }) =>
    variant === "light"
      ? `background:#eef2f6;color:#25364a;&:hover{background:#e4e9ef;}`
      : `background:${BRAND};color:#fff;&:hover{background:${BRAND_DARK};}`}
  &:disabled { opacity: .6; cursor: not-allowed; pointer-events: none; }
  &:active { transform: translateY(1px); }
`;

export const LinkLike = styled.span`
  font-size: 13px; color: ${BRAND_DARK}; text-decoration: underline; cursor: pointer; user-select: none;
`;

export const Alert = styled.div<{ tone?: "danger" | "success" }>`
  margin-top: 8px; padding: 10px 12px; border-radius: 10px; font-size: 14px;
  ${({ tone }) =>
    tone === "success"
      ? `background:#E8F5E9;color:#2E7D32;border:1px solid #C8E6C9;`
      : `background:#FDECEA;color:#C62828;border:1px solid #FFCDD2;`}
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
export const Spinner = styled(AiOutlineLoading3Quarters)`
  animation: ${spin} 0.9s linear infinite;
  font-size: 18px;
  vertical-align: middle;
`;


// Scope password-step spacing and scrollbar reset to the login card.
export const PasswordFlowForm = styled(LoginForm)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  text-align: left;
  ${Field}, ${Hint}, ${Alert} { margin-bottom: 0; margin-top: 0; }
  ${Title} { margin: 0; text-align: left; }
  > ${Row} { margin-top: 4px; }
  > ${Hint}[role="note"] { line-height: 1.6; }
`;
